import { Alert, Flex, Group, Loader, Space, Table, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useGetProjetoItem from "@/modules/diagnostico/components/questionario-item/useGetProjeto";
import { RankingListPaginator } from "@/modules/diagnostico/components/ranking-list/components/RankingListPaginator";
import { usePaginator } from "@/modules/diagnostico/components/ranking-list/hooks/usePaginator";
import useUserStore from "@/modules/users/store";
import ExportExcel from "./ExportExcel";
import ModalFilter from "./ModalFilter";
import { getRelatorioColaboradores } from "../../functions";
import TableColaboradoresItem from "../table-colaboradores-item";

interface Filtro {
  search: string;
  filtro: string;
  habilitada: boolean;
}

export type EmpresaRelatorio = {
  id: string;
  nomeFantasia: string;
  cnpj: string;
  responsavel: {
    nome: string;
    email: string;
    telefone: string;
  };
  telefone: string;
  porte: string;
  endereco: {
    estado: string;
    cidade: string;
  };
  nColaboradores: number;
  nRespostas: number;
  habilitada: boolean;
  minimoResposta?: number;
};

const STALE_TIME_MINUTES = 5;
const GC_TIME_MINUTES = 10;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const MILLISECONDS_PER_MINUTE = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
const EMPTY_STRING_FALLBACK = "";

const mockFilter = {
  search: "",
  filtro: "",
  habilitada: false
};

const TableColaboradores = ({ questionario }: { questionario: string }) => {
  const projeto = useUserStore((state) => state.activeProjeto);
  const [filter, setFilter] = useState<Filtro>(mockFilter);

  const { data: projetoData } = useGetProjetoItem({ idProjeto: projeto || "" });

  const {
    data: empresasComDados = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ["empresas-detalhamento", projeto, questionario],
    queryFn: () =>
      getRelatorioColaboradores({
        projetoId: projeto || "",
        questionarioId: questionario,
        licenciadaId: projetoData?.licenciado || ""
      }),
    enabled: Boolean(projeto && questionario && projetoData?.licenciado),
    staleTime: STALE_TIME_MINUTES * MILLISECONDS_PER_MINUTE,
    gcTime: GC_TIME_MINUTES * MILLISECONDS_PER_MINUTE
  });

  const dados = useMemo(() => filteredData(empresasComDados, filter), [empresasComDados, filter]);

  const { currentPageContent, currentPage, pageSize, setCurrentPage, setPageSize } = usePaginator(dados);

  const rows = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    () => currentPageContent.map((item) => <TableColaboradoresItem key={item.id} item={item as any} />),
    [currentPageContent]
  );

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size="1rem" />} title="Erro ao carregar dados" color="red">
        Não foi possível carregar os dados dos colaboradores. Por favor, tente novamente.
      </Alert>
    );
  }

  return (
    <>
      <Group justify={"space-between"} mb={10}>
        <Text size={"md"} fw={700}>
          Detalhamento das inscrições
        </Text>
        <Group>
          <ModalFilter setFilter={setFilter} />
          <ExportExcel empresas={dados as any} />
        </Group>
      </Group>
      <div style={{ overflowX: "auto" }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Empresa</Table.Th>
              <Table.Th>Cnpj</Table.Th>
              <Table.Th>Responsavel</Table.Th>
              <Table.Th>Telefone</Table.Th>
              <Table.Th>Porte</Table.Th>
              <Table.Th>Estado</Table.Th>
              <Table.Th>Nº Convidados</Table.Th>
              <Table.Th>Nº Respostas</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{isLoading ? <></> : rows}</Table.Tbody>
        </Table>
        <Flex p="sm" justify="flex-end" gap="sm">
          <RankingListPaginator
            numberOfItems={dados.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </Flex>
      </div>
      <Space h={"md"} />
      {isLoading ? (
        <Group justify="center" align="center">
          <Loader color="blue" />
        </Group>
      ) : null}
    </>
  );
};

export default TableColaboradores;

const getFieldValue = (item: EmpresaRelatorio, field: string): string => {
  const fieldMap: Record<string, string> = {
    Empresa: item.nomeFantasia,
    Cnpj: item.cnpj,
    Responsavel: item.responsavel.nome,
    Telefone: item.telefone,
    Porte: item.porte,
    Estado: item.endereco.estado,
    "Nº Convidados": item.nColaboradores.toString()
  };
  return fieldMap[field] ?? EMPTY_STRING_FALLBACK;
};

const doFilter = (item: EmpresaRelatorio, filter: Filtro): boolean => {
  if (!filter.filtro) {
    return filter.habilitada ? item.habilitada : true;
  }

  const fieldValue = getFieldValue(item, filter.filtro);
  const matchesSearch = fieldValue === filter.search;

  return filter.habilitada ? matchesSearch && item.habilitada : matchesSearch;
};

const filteredData = (data: EmpresaRelatorio[], filter: Filtro) => {
  return data.filter((item) => doFilter(item, filter)).sort((a, b) => b.nRespostas - a.nRespostas);
};
